import { useEffect, useState } from 'react';
import {
  Datagrid,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  required,
  SelectInput,
  TextInput,
  TopToolbar,
  useNotify,
  useTranslate,
} from 'react-admin';
import { Box, Card, Chip, Tooltip, Typography } from '@mui/material';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import { ProviderItem, listProviders } from '../../../services/xbitRelayService';
import {
  DEFAULT_DEPOSIT_WALLETS_PROVIDER,
  XBIT_RELAY_DEPOSIT_WALLETS_PERMISSION_RESOURCE,
} from './constants';
import './init';

const truncate = (value: string, head = 8, tail = 6) =>
  value && value.length > head + tail + 3
    ? `${value.slice(0, head)}...${value.slice(-tail)}`
    : value || '-';

const DepositWalletsListActions = ({ providers }: { providers: ProviderItem[] }) => (
  <TopToolbar
    sx={{
      minHeight: 90,
      alignItems: 'center',
      px: 3,
      pt: 1,
      overflow: 'visible',
      width: '100%',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap',
        '& form': { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%' },
        '& .ra-input': { mt: 0, mb: 0 },
        '& .MuiFormControl-root': { mt: 0, mb: 0 },
        '& .MuiInputBase-root': { minHeight: 40 },
      }}
    >
      <FilterForm
        filters={[
          <SelectInput
            key="provider"
            source="provider"
            label="module.xbitRelayDepositWallets.provider"
            choices={providers.map((p) => ({ id: p.provider, name: p.provider }))}
            alwaysOn
            size="small"
            sx={{ minWidth: 200 }}
            validate={required()}
            isRequired
          />,
          <TextInput
            key="userId"
            source="userId"
            label="module.xbitRelayDepositWallets.userId"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 240 }}
          />,
          <TextInput
            key="address"
            source="address"
            label="module.xbitRelayDepositWallets.address"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 240 }}
          />,
        ]}
      />
    </Box>
  </TopToolbar>
);

const XbitRelayDepositWalletsList = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [providers, setProviders] = useState<ProviderItem[]>([]);

  useEffect(() => {
    listProviders()
      .then((res) => setProviders(res.data?.providers ?? []))
      .catch(() => setProviders([]));
  }, []);

  const copy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    notify('Copied to clipboard', { type: 'info' });
  };

  return (
    <DashboardContent>
      <PermissionGuard
        resource={XBIT_RELAY_DEPOSIT_WALLETS_PERMISSION_RESOURCE}
        action="read"
        fallback={<ErrorPage403 />}
      >
        <CustomBreadcrumbs
          heading={t('module.xbitRelayDepositWallets.title')}
          links={[
            { name: t('resource.xbitRelay'), href: '#' },
            { name: t('module.xbitRelayDepositWallets.title') },
            { name: t('resource.list') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={<DepositWalletsListActions providers={providers} />}
            empty={false}
            perPage={20}
            exporter={false}
            filterDefaultValues={{ provider: DEFAULT_DEPOSIT_WALLETS_PROVIDER }}
            pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100]} />}
            sort={{ field: 'address', order: 'ASC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={<EmptyContent />}
                sx={{
                  '& .RaDatagrid-headerCell': { fontWeight: 600 },
                  '& .RaDatagrid-rowCell': { py: 1.25 },
                }}
              >
                <FunctionField
                  label="module.xbitRelayDepositWallets.address"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={record.address || ''}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        onClick={() => copy(record.address)}
                      >
                        {truncate(record.address)}
                      </Typography>
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDepositWallets.chain"
                  sortable={false}
                  render={(record: any) => (
                    <Chip label={record.chain || '-'} size="small" variant="outlined" />
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDepositWallets.provider"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {record.provider || '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDepositWallets.targetAddress"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={record.targetAddress || ''}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        onClick={() => copy(record.targetAddress)}
                      >
                        {truncate(record.targetAddress)}
                      </Typography>
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDepositWallets.userId"
                  sortable={false}
                  render={(record: any) =>
                    record.userId ? (
                      <Tooltip title={record.userId}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() => copy(record.userId)}
                        >
                          {truncate(record.userId)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label="module.xbitRelayDepositWallets.index"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {record.index ?? '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayDepositWallets.hdPath"
                  sortable={false}
                  render={(record: any) =>
                    record.hdPath ? (
                      <Tooltip title={record.hdPath}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          onClick={() => copy(record.hdPath)}
                        >
                          {record.hdPath}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default XbitRelayDepositWalletsList;
